// centralized date utility for consistent timezone-safe date handling
import { format, addMonths as dfAddMonths, subDays as dfSubDays, isValid } from 'date-fns';
import { TZDate } from '@date-fns/tz';
import { UTCDate } from '@date-fns/utc';

export const getUserTZ = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// -- PARSING & VALIDATION --

// parse any date value into a valid Date object or null
export const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return isValid(value) ? value : null;
    const d = new Date(value);
    return isValid(d) ? d : null;
};

// check if a value can be parsed as a valid date
export const isValidDate = (value) => parseDate(value) !== null;

// -- DISPLAY: Calendar dates (always interpret in UTC, no TZ shift) --

// "March 15, 2024" -- full month name, always UTC
export const formatUTCDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return 'N/A';
    return format(new UTCDate(d), 'MMMM d, yyyy');
};

// "Mar 15, 2024" -- short month, always UTC
export const formatUTCDateShort = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return 'N/A';
    return format(new UTCDate(d), 'MMM dd, yyyy');
};

// "2024-03-15" -- for <input type="date"> value, always UTC
export const dateToInput = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return '';
    return format(new UTCDate(d), 'yyyy-MM-dd');
};

// "03-15-2024" -- MM-DD-YYYY display format, always UTC
export const formatUTCDateDash = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return 'N/A';
    return format(new UTCDate(d), 'MM-dd-yyyy');
};

// -- DISPLAY: Timestamps (show in user's local timezone) --

// "March 15, 2024" -- full date in user's TZ
export const formatDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return 'N/A';
    return format(new TZDate(d, getUserTZ()), 'MMMM d, yyyy');
};

// "Mar 15, 2024 3:45 PM" -- date and time in user's TZ
export const formatDateTime = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return 'N/A';
    return format(new TZDate(d, getUserTZ()), 'MMM dd, yyyy h:mm a');
};

// "03/15/2024" -- compact slash format in user's TZ
export const formatDateSlash = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return 'N/A';
    return format(new TZDate(d, getUserTZ()), 'MM/dd/yyyy');
};

// -- SENDING TO API --

// converts date input value "2024-03-15" to noon UTC ISO string
export const dateInputToUTC = (dateInputValue) => {
    if (!dateInputValue) return null;
    const match = String(dateInputValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return new Date(`${match[1]}-${match[2]}-${match[3]}T12:00:00.000Z`).toISOString();
};

// converts a Date object to ISO string
export const toISO = (date) => {
    const d = parseDate(date);
    return d ? d.toISOString() : null;
};

// returns current moment as UTC ISO string
export const nowISO = () => new Date().toISOString();

// -- DATE MATH --

// returns a new Date offset by N months from the given date
export const addMonths = (date, months) => dfAddMonths(new Date(date), months);

// returns a Date N days before now
export const daysAgo = (n) => dfSubDays(new Date(), n);

// returns { start, end } Date objects for a date range
export const getRange = ({ startDate, endDate, daysBack }) => {
    if (startDate && endDate) {
        return { start: new Date(startDate), end: new Date(endDate) };
    }
    return { start: daysAgo(daysBack), end: new Date() };
};

// validates and normalizes a calendar date string to noon UTC Date object
export const normalizeCalendarDate = (value) => {
    if (!value) return null;
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const date = new Date(`${match[1]}-${match[2]}-${match[3]}T12:00:00.000Z`);
    return isNaN(date.getTime()) ? null : date;
};

// returns UTC day start and end for date range queries
export const getUTCDayBounds = () => {
    const now = new Date();
    return {
        start: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0)),
        end: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999))
    };
};
